import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Popconfirm, Select, TimePicker, Row, Col, Spin } from 'antd';
import { useStandbyScheduleDetail, useRefCode } from 'hooks';
import { DatePicker, Lecturer, LectureSubject, CompanySelectBox, Pm } from 'components/atoms'
import moment, { Moment } from 'moment';
import { fetcher } from 'services';
import { dateFormat } from 'utils/date-utils';
import './index.scss';
import {
    CheckDuplicateModal,
    ProjectAskSelectModal,
    ProjectAskEditModal
} from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { getCodeNumberArray } from 'utils/form-utils';

export interface StandbyScheduleEditModalProps extends PopupProps {
    id: number;
    schedule_date?: any;
    schedule_start_time?: any;
    schedule_end_time?: any;
    view_mode?: boolean;
}

const StandbyScheduleEditModal = ({ 
    isOpen, 
    onClose, 
    id, schedule_date, schedule_start_time, schedule_end_time, view_mode = false}: StandbyScheduleEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [isCheckConfirm, setIsCheckConfirm] = useState<boolean>(false);   
    const [checkMessage, setCheckMessage] = useState<string>(''); 
    const [scheduleSrl, setScheduleSrl] = useState<number>(id);
    const modalRef = useRef<null | HTMLDivElement>(null);
    const [startTime, setStartTime] = useState<string>('09:00');
    const [endTime, setEndTime] = useState<string>('09:00');   
    const [saveData, setSaveData] = useState<any>(); 
    const [companySrl, setCompanySrl] = useState<number>(0);
    const [askGroupSrl, setAskGroupSrl] = useState<number>(0);    
    const [projectType, setProjectType] = useState<string>('none');
    const [projectGroupSrl, setProjectGroupSrl] = useState<any>();    

  /* ======================================================
  * hooks
  * ====================================================== */
  const {
        isLoading, 
        isLoadingInsert,
        isLoadingUpdate,
        isLoadingDelete,    
        scheduleInfo, 
        lastInsertSrl, 
        projectList,
        insertStandbySuccess, 
        updateStandbySuccess, 
        deleteStandbySuccess, 
        standbyScheduleInsert, 
        standbyScheduleUpdate, 
        standbyScheduleDelete,
        standbyProjectList
    } = useStandbyScheduleDetail(scheduleSrl);
  const { refCodeList: lectureTypeList } = useRefCode({field:'lecture_type1'});   
  const [ lectureTypeCodeList, setlectureTypeCodeList] = useState<RefCodeListDto[] | any[]>();

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { open } = usePopupManager(); 
   /* ======================================================
   * events
   * ====================================================== */
    
    const onCancel = () => {
        onClose?.(insertStandbySuccess || updateStandbySuccess ||deleteStandbySuccess);
    };

    const onSubmit = (e) => {
        if (!!scheduleInfo || scheduleSrl == 0) {

            const sData = {
                id: scheduleSrl,
                project_group_srl: projectGroupSrl || 0, 
                memo: e.memo,
                lecturer_srl: e.lecturer_srl,
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: endTime < startTime? moment(form.getFieldValue('schedule_date')).add(1, 'days').format('YYYY-MM-DD')  + ' ' + endTime : dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime, 
                time_count: e.time_count,
                lecture_type1: e.lecture_type, 
                lecture_subject1: e.lecture_subject1,
                lecture_subject_memo: e.lecture_subject_memo,
                ask_group_srl: askGroupSrl,
                company_srl: companySrl,
                pm: form.getFieldValue('pm') ? form.getFieldValue('pm').map((code) => code).join(',') : '',
            }
            setSaveData(sData);

            const preStartTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.start_time;
            const preEndTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.end_time;

            if ((sData.start_time && sData.end_time && sData.lecturer_srl) &&
                (sData.project_group_srl > 0 || scheduleSrl == 0 || sData.start_time != preStartTime || sData.end_time != preEndTime || sData.lecturer_srl != scheduleInfo?.lecturer_srl)) {
                // 중복 검사
                fetcher.project.postProjectScheduleCheck({
                    id: scheduleSrl,
                    lecture_studio: 'ETC',
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time,
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            if (scheduleSrl > 0) {
                                standbyScheduleUpdate(sData)
                            } else {
                                standbyScheduleInsert(sData)
                            }                                
                        break;
                        default:  
                            if (projectType === 'confirm' && projectGroupSrl && projectGroupSrl > 0) {
                                alert("대기 스케줄은 확정 프로젝트와 연결 할수 없습니다.");
                            } else {                        
                                if (res.message.startsWith('[확정]')) {
                                    setCheckMessage(res.message);
                                } else {
                                    setCheckMessage(res.message + '\n\n' + '대기로 입력하시겠습니까?');
                                }
                                
                                setIsCheckConfirm(true);
                            }
                        break;
                    };
                });                 
            } else {
                if (scheduleSrl > 0) {
                    standbyScheduleUpdate(sData)
                } else {
                    standbyScheduleInsert(sData)
                }  
            }
        }
 
    };

    const onSelectAskPopup = () => {
        if (companySrl == 0) {
            alert("고객사를 선택해주세요.");   
            return;   
        }
        if (askGroupSrl > 0) {
            open(ProjectAskEditModal, {
                id: askGroupSrl,
            }); 
        } else {
            open(ProjectAskSelectModal, {
                company_srl: companySrl,
                onClose: (isOk, askId) => {
                    if (isOk) {
                        setAskGroupSrl(askId);
                    }
                },
            }); 
        }
    }      

   const onCopy = () => {
       // console.log("onCopy");
        if (!!scheduleInfo) {
           const sData = {
                id: 0,
                project_group_srl: projectGroupSrl || 0,
                memo: form.getFieldValue('memo'),
                lecturer_srl: form.getFieldValue('lecturer_srl'),
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: endTime < startTime? moment(form.getFieldValue('schedule_date')).add(1, 'days').format('YYYY-MM-DD')  + ' ' + endTime : dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime, 
                time_count: form.getFieldValue('time_count'), 
                lecture_type1: form.getFieldValue('lecture_type'), 
                lecture_subject1: form.getFieldValue('lecture_subject1'),
                lecture_subject_memo: form.getFieldValue('lecture_subject_memo'),
                ask_group_srl: askGroupSrl,
                company_srl: companySrl,
                pm: form.getFieldValue('pm') ? form.getFieldValue('pm').map((code) => code).join(',') : '',
            }
            setSaveData(sData);
            if (sData.start_time && sData.end_time && sData.lecturer_srl) {
                // 중복 검사
                fetcher.project.postProjectScheduleCheck({
                    id: 0,
                    lecture_studio: 'ETC',
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time,
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            standbyScheduleInsert(sData)                               
                        break;
                        default:  
                            if (projectType === 'confirm' && projectGroupSrl && projectGroupSrl > 0) {
                                alert("대기 스케줄은 확정 프로젝트와 연결 할수 없습니다.");
                            } else {
                                if (res.message.startsWith('[확정]')) {
                                    setCheckMessage(res.message);
                                } else {
                                    setCheckMessage(res.message + '\n\n' + '대기로 입력하시겠습니까?');
                                }
                                
                                setIsCheckConfirm(true);
                            }
                        break;
                    };
                });                 
            } else {
                standbyScheduleInsert(sData) 
            }
        }
    }    
  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!scheduleInfo) {
            form.setFieldsValue({
                memo: scheduleInfo.memo,
                lecturer_srl: scheduleInfo.lecturer_srl,
                schedule_date: (!!scheduleInfo.schedule_date && scheduleInfo.schedule_date !== '0000-00-00') ? moment(scheduleInfo.schedule_date, "YYYY-MM-DD") : undefined,
                lecture_time: scheduleInfo.start_time ? [moment(scheduleInfo.start_time, 'HH:mm'), moment(scheduleInfo.end_time, 'HH:mm')]: [] ,
                time_count: scheduleInfo.time_count, 
                lecture_type: scheduleInfo.lecture_type1,
                lecture_subject1: scheduleInfo.lecture_subject1,
                lecture_subject_memo: scheduleInfo.lecture_subject_memo,
                companySrl: scheduleInfo.company_srl,
                pm: getCodeNumberArray(scheduleInfo.pm)
            });
            setStartTime(scheduleInfo.start_time?scheduleInfo.start_time:"09:00");
            setEndTime(scheduleInfo.end_time?scheduleInfo.end_time:"09:00");
            setAskGroupSrl(scheduleInfo.ask_group_srl);
            setCompanySrl(scheduleInfo.company_srl);
        }

        const options = lectureTypeList?.filter((item) => item.status_type === 'AV' || (scheduleInfo && scheduleInfo.lecture_type1 == item.name)) || [];
        setlectureTypeCodeList(options);

    }, [scheduleInfo, lectureTypeList])

    useEffect(() => {
        if (insertStandbySuccess || updateStandbySuccess || deleteStandbySuccess) onClose?.(true);

    }, [insertStandbySuccess, updateStandbySuccess, deleteStandbySuccess])

    useEffect(() => {
        form.setFieldsValue({
            schedule_date: (!!schedule_date && schedule_date !== '0000-00-00') ? moment(schedule_date, "YYYY-MM-DD") : undefined,
            lecture_time: schedule_start_time ? [moment(schedule_start_time, 'HH:mm'), moment(schedule_end_time, 'HH:mm')]: [] ,
            time_count: schedule_start_time && schedule_end_time ? moment(schedule_end_time, 'HH:mm').diff(moment(schedule_start_time, 'HH:mm'), 'hours', true): 1
        });
        setStartTime(schedule_start_time?schedule_start_time:"09:00");
        setEndTime(schedule_end_time?schedule_end_time:"09:00");        
    }, [])    

    useEffect(() => {
        setProjectGroupSrl(undefined)
        if (projectType !== 'none') {            
            standbyProjectList({
                group:projectType,
                searchDateEnd: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                company: companySrl > 0 ? companySrl.toString() : ''
            })
        };
    }, [projectType])        
    
    return (
        <>
            <ModalWrapper open={isOpen} onCancel={onCancel} width={750} title={scheduleSrl===0?'일정 등록':'일정 수정'} closable>
                <div style={{width:"100%"}} ref={modalRef}>
                <Spin spinning={isLoading || isLoadingInsert || isLoadingUpdate || isLoadingDelete} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
                <Form
                    form={form}
                    name="ProjectInfo"
                    layout="horizontal"  
                    labelCol={{ flex: '120px' }}
                    onFinish={onSubmit}
                    style={{width:'100%'}}
                    autoComplete="off"
                >
                    <Form.Item  label={<label className={'ant-form-item-required'}>고객사(기업명)</label>} className={'ant-form-item-required'}>
                        <Form.Item name="company_srl" rules={[{ required: true, message: '' }]} style={{ width:'auto', display: 'inline-block' }}>
                            <CompanySelectBox required={true} form={form} company_srl={companySrl} onSelected={(value, option) => {setCompanySrl(Number(value));}} />
                        </Form.Item>  
                        <Form.Item style={{ width: 'auto', display: 'inline-block'}}>
                            <Button style={{marginLeft:'5px'}} onClick={onSelectAskPopup} type="primary" danger = {askGroupSrl === 0}>{askGroupSrl === 0 ? `문의 연결` : `문의 보기`}</Button>
                            {(askGroupSrl > 0) && (
                                <Button style={{marginLeft:'5px'}} onClick={()=>setAskGroupSrl(0)} type="primary" danger = {true}>{`문의 삭제`}</Button>
                            )}
                        </Form.Item>                    
                    </Form.Item >      
                    <Form.Item  label={'프로젝트'}>
                        <Row>
                            <Col span={4}>
                                <Select defaultValue={projectType} onChange={(value)=>setProjectType(value)}>
                                    <Select.Option key={'none'} value={'none'}>미연결</Select.Option>
                                    <Select.Option key={'confirm'} value={'confirm'}>확정</Select.Option>
                                    <Select.Option key={'consult'} value={'consult'}>논의중</Select.Option>
                                </Select>                             
                            </Col>
                            <Col span={20}>
                                {projectType !== 'none' && (
                                <Select 
                                    placeholder={'프로젝트를 선택하세요.'} 
                                    style={{marginLeft:5}} 
                                    value={projectGroupSrl}
                                    onChange={(value, option: any)=>{
                                        setProjectGroupSrl(value);
                                        setCompanySrl(option.option.company_srl)
                                    }}
                                >
                                    {projectList?.map((item) => {
                                        return (<Select.Option key={item.project_group_srl} value={item.project_group_srl} option={item}>{item.title}</Select.Option>)
                                    })}                                     
                                </Select>                              
                                )}
                            </Col>                            
                        </Row>
                    </Form.Item >                                     
                    <Form.Item name='memo' label={'일정명'} rules={[{ required: true, message: '' }]}>
                        <Input placeholder='일정명를 입력하세요'/>   
                    </Form.Item>                      
                    <Form.Item name="lecturer_srl" label={'강사'} rules={[{ required: true, message: '' }]}>
                        <Lecturer form={form} required={true}/>
                    </Form.Item> 
                    <Form.Item 
                        name="schedule_date" 
                        label={'교육 일자'}
                        rules={[{ required: true, message: '' }]}
                    >
                        <DatePicker showTime={false} format="YYYY-MM-DD" />                    
                    </Form.Item>            
                    <Form.Item name="lecture_time" label={'강의 시간'} rules={[{ required: true, message: '' }]}>
                        <Form.Item style={{ display: 'inline-block', marginRight: '5px'}}>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(startTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setStartTime(timeString);
                            }}
                            style={{width:80}}
                        />  
                        <label>&nbsp;~&nbsp;</label>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(endTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setEndTime(timeString);
                            }}
                            style={{width:80}}
                        />   
                        </Form.Item>  
                        <Form.Item name="time_count" style={{ width:'50px', display: 'inline-block', marginRight: '5px' }} rules={[{ required: true, message: '' }]}>
                            <Input type="number" placeholder='시간'/>  
                        </Form.Item>   
                        <Form.Item style={{ display: 'inline-block'}}>
                        <label>시간</label>                                           
                        </Form.Item>                                              
                    </Form.Item> 
                    <Form.Item  label={<label className={'ant-form-item-required'}>PM</label>}>
                        <Pm fieldname={'pm'} pm={scheduleInfo?.pm} required={true} mode={'multiple'}/>                       
                    </Form.Item>
                    <Form.Item name="lecture_type" label={'강의 구분'}>
                        <Select 
                            placeholder="강의구분 선택"
                        >
                        {lectureTypeCodeList?.map((item) => {
                            return (<Select.Option key={item.name} value={item.name} option={item.option}>{item.value}</Select.Option>)
                        })}                        
                        </Select>                                        
                    </Form.Item>                      
                    <Form.Item label={'강의 주제'}>
                        <LectureSubject form={form} />
                    </Form.Item>     
                    {(!view_mode) && (
                        <ButtonGroup className={'buttonsWrapper'}>
                            <div>
                                <Button onClick={onCancel} style={{marginRight: "5px"}}>닫기</Button>
                                {(scheduleSrl>0) && (
                                <Popconfirm
                                    title="시간 수정 하였습니까?"
                                    okText="복사"
                                    cancelText="취소"
                                    onConfirm={() => { onCopy()}}
                                >   
                                    <Button>복사</Button>
                                </Popconfirm>                       
                                )}                                
                            </div>   
                            <div>
                                {(scheduleSrl>0 && !!scheduleInfo) && (
                                    <Popconfirm
                                        title="삭제 하시겠습니까?"
                                        okText="삭제"
                                        cancelText="취소"
                                        onConfirm={() => { standbyScheduleDelete({id:scheduleSrl})}}
                                    >   
                                        <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                                    </Popconfirm>
                                )}
                                <Button htmlType="submit"  type="primary">{scheduleSrl===0?'등록':'수정'}</Button>
                            </div>                    
                        </ButtonGroup>    
                    )}  
                </Form> 
                </Spin>               
                </div>
            </ModalWrapper>  
            <CheckDuplicateModal
                title="스케줄 확인"
                message={checkMessage}
                visible={isCheckConfirm}
                onOk={() => {
                    setIsCheckConfirm(false)
                    if (!checkMessage.startsWith('[확정]')) {
                        if (saveData.id > 0) {
                            standbyScheduleUpdate(saveData)
                        } else {
                            standbyScheduleInsert(saveData)
                        }  
                    }
                }}
                onCancel={
                    checkMessage.startsWith('[확정]') ? undefined : ()=>setIsCheckConfirm(false)
                }
            />          
        </>
    )    
};

export default React.memo(StandbyScheduleEditModal);    